import type { ReactNode } from 'react';
import {
  FIT_MODES,
  SHAPE_KINDS,
  TEXT_ALIGNMENTS,
  type Layer,
  type FitMode,
  type ShapeKind,
  type TextAlignment,
} from '@liveforge/shared';

/** A track the host can point a video or screen-share layer at. */
export interface AvailableTrack {
  sid: string;
  participantIdentity: string;
  trackName: string;
  label: string;
  isScreenShare: boolean;
}

export interface LayerPropertiesProps {
  layer: Layer | undefined;
  tracks: AvailableTrack[];
  onUpdate: (id: string, patch: Partial<Layer>) => void;
}

/**
 * A collapsible group.
 *
 * `details` rather than a button and a flag: it collapses without JavaScript,
 * it is keyboard reachable for free, and the browser already knows how to
 * announce it.
 */
function Section({
  title,
  children,
  open = true,
}: {
  title: string;
  children: ReactNode;
  open?: boolean;
}) {
  return (
    <details className="prop-section" open={open}>
      <summary className="prop-section__head">
        <span>{title}</span>
      </summary>
      <div className="prop-section__body">{children}</div>
    </details>
  );
}

/** One labelled control. The label sits above, so two fit side by side. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="prop-field">
      <span className="prop-field__label muted">{label}</span>
      {children}
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  disabled,
  step = 1,
  min,
  max,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled: boolean;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  return (
    <Field label={suffix ? `${label} (${suffix})` : label}>
      <input
        className="input input--small"
        type="number"
        value={Number.isFinite(value) ? value : 0}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
        // An emptied field parses as NaN, which would poison the layer and the
        // canvas with it. It is treated as zero until the host types a digit.
        onChange={(event) => onChange(Number.parseFloat(event.target.value) || 0)}
      />
    </Field>
  );
}

/**
 * A slider with the number beside it.
 *
 * Both, not one or the other: dragging is how a value like opacity is actually
 * chosen, and typing is how it is matched to another layer. A slider alone
 * cannot be set to exactly 0.5, and a number alone gives no sense of where 0.5
 * sits between the ends.
 */
function SliderField({
  label,
  value,
  onChange,
  disabled,
  min,
  max,
  step = 1,
  format,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled: boolean;
  min: number;
  max: number;
  step?: number;
  /** How the readout reads, when the raw number is not the useful form. */
  format?: (value: number) => string;
  /** Unit shown against the number box, for values that have one. */
  suffix?: string;
}) {
  const safe = Number.isFinite(value) ? value : min;
  return (
    <div className="prop-field">
      <span className="prop-field__label muted">
        {label}
        {format ? <span className="prop-field__value">{format(safe)}</span> : null}
      </span>
      <span className="prop-slider">
        <input
          className="prop-slider__range"
          type="range"
          value={safe}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={(event) => onChange(Number.parseFloat(event.target.value))}
        />
        <input
          className="input input--small prop-slider__number"
          type="number"
          value={safe}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={(event) => onChange(Number.parseFloat(event.target.value) || 0)}
        />
        {suffix ? <span className="prop-slider__suffix muted">{suffix}</span> : null}
      </span>
    </div>
  );
}

/**
 * A short list of choices, shown as choices.
 *
 * Three fit modes behind a dropdown means two clicks and a memory test to see
 * what the alternatives even are. Laid out, it is one click and no memory.
 */
function SegmentedField<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: readonly T[];
  onChange: (value: T) => void;
  disabled: boolean;
}) {
  return (
    <div className="prop-field">
      <span className="prop-field__label muted">{label}</span>
      <div className="segmented" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={`segmented__option ${option === value ? 'segmented__option--on' : ''}`}
            disabled={disabled}
            aria-pressed={option === value}
            onClick={() => onChange(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  placeholder?: string;
}) {
  return (
    <Field label={label}>
      <input
        className="input input--small"
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

function ColorField({
  label,
  value,
  onChange,
  disabled,
  allowNone,
}: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled: boolean;
  allowNone: boolean;
}) {
  return (
    <Field label={label}>
      <span className="prop-field__color">
        {allowNone ? (
          <input
            type="checkbox"
            checked={value !== null}
            disabled={disabled}
            title={value === null ? 'Enable' : 'Disable'}
            onChange={(event) => onChange(event.target.checked ? '#ffffff' : null)}
          />
        ) : null}
        <input
          className="input input--color"
          type="color"
          value={value ?? '#000000'}
          disabled={disabled || value === null}
          onChange={(event) => onChange(event.target.value)}
        />
      </span>
    </Field>
  );
}

function CheckField({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
}) {
  return (
    <label className="prop-check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

/**
 * Properties of the selected layer, grouped the way a director reaches for
 * them: what it shows, where it sits, how it looks.
 *
 * Every control is disabled when the layer is locked - that is the whole
 * meaning of the lock, and disabling is better than hiding because the host
 * can still see what the values are.
 */
export function LayerProperties({ layer, tracks, onUpdate }: LayerPropertiesProps) {
  if (!layer) {
    return <p className="muted prop-panel__empty">Select a layer to edit it.</p>;
  }

  const locked = layer.locked;
  const patch = (values: Partial<Layer>) => onUpdate(layer.id, values);
  const hasSource =
    layer.type === 'video' ||
    layer.type === 'screenshare' ||
    layer.type === 'image' ||
    layer.type === 'text' ||
    layer.type === 'template';

  return (
    <div className="prop-panel">
      {locked ? (
        <p className="prop-note prop-note--locked">
          This layer is locked. Unlock it from the layer list to make changes.
        </p>
      ) : null}

      <div className="prop-panel__name">
        <TextField
          label="Name"
          value={layer.name}
          disabled={locked}
          onChange={(name) => patch({ name })}
        />
      </div>

      {hasSource ? (
        <Section title="Source">
          {layer.type === 'video' || layer.type === 'screenshare' ? (
            <Field label="Track">
              <select
                className="select select--small"
                disabled={locked}
                value={layer.trackSid ?? ''}
                onChange={(event) => {
                  const found = tracks.find((track) => track.sid === event.target.value);
                  patch({
                    trackSid: found?.sid ?? null,
                    participantIdentity: found?.participantIdentity ?? null,
                    ...(layer.type === 'video' ? { trackName: found?.trackName ?? null } : {}),
                  });
                }}
              >
                <option value="">Not assigned</option>
                {tracks
                  .filter((track) => track.isScreenShare === (layer.type === 'screenshare'))
                  .map((track) => (
                    <option key={track.sid} value={track.sid}>
                      {track.label}
                    </option>
                  ))}
              </select>
            </Field>
          ) : null}

          {layer.type === 'image' ? (
            <TextField
              label="URL"
              value={layer.url}
              disabled={locked}
              placeholder="https://..."
              onChange={(url) => patch({ url })}
            />
          ) : null}

          {layer.type === 'text' ? (
            <Field label="Text">
              <textarea
                className="input input--small"
                rows={3}
                value={layer.text}
                disabled={locked}
                onChange={(event) => patch({ text: event.target.value })}
              />
            </Field>
          ) : null}

          {layer.type === 'template' ? (
            <p className="muted prop-note">
              Template layers are declared now so the composition does not change shape later, but
              they are not rendered until Phase 7.
            </p>
          ) : null}
        </Section>
      ) : null}

      {layer.type === 'auto' ? (
        <Section title="Source">
          <p className="prop-note muted">
            Everyone in the room, arranged automatically. Nobody is named here, so anyone who joins
            appears at once - even while this scene is on air.
          </p>
        </Section>
      ) : null}

      <Section title="Size and position">
        <div className="prop-grid">
          <NumberField label="X" value={layer.x} disabled={locked} onChange={(x) => patch({ x })} />
          <NumberField label="Y" value={layer.y} disabled={locked} onChange={(y) => patch({ y })} />
          <NumberField
            label="Width"
            value={layer.width}
            disabled={locked}
            min={1}
            onChange={(width) => patch({ width })}
          />
          <NumberField
            label="Height"
            value={layer.height}
            disabled={locked}
            min={1}
            onChange={(height) => patch({ height })}
          />
          <div className="prop-grid__wide">
            <SliderField
              label="Rotation"
              value={layer.rotation}
              disabled={locked}
              min={-180}
              max={180}
              format={(value) => `${Math.round(value)}°`}
              onChange={(rotation) => patch({ rotation: Math.round(rotation) })}
            />
          </div>
        </div>
      </Section>

      <Section title="Appearance">
        <div className="prop-grid">
          <div className="prop-grid__wide">
            <SliderField
              label="Opacity"
              suffix="%"
              // Stored 0-1, edited 0-100: nobody says "point three five opacity".
              value={Math.round(layer.opacity * 100)}
              disabled={locked}
              min={0}
              max={100}
              onChange={(percent) => patch({ opacity: Math.min(1, Math.max(0, percent / 100)) })}
            />
          </div>

          {layer.type === 'video' || layer.type === 'screenshare' || layer.type === 'auto' ? (
            <SegmentedField
              label="Fit"
              value={layer.fit}
              options={FIT_MODES}
              disabled={locked}
              onChange={(fit) => patch({ fit })}
            />
          ) : null}

          {layer.type === 'image' ? (
            <SegmentedField
              label="Fit"
              value={layer.fit}
              options={FIT_MODES}
              disabled={locked}
              onChange={(fit) => patch({ fit })}
            />
          ) : null}

          {layer.type === 'auto' ? (
            <>
              <SliderField
                label="Gap"
                value={layer.gap}
                disabled={locked}
                min={0}
                max={200}
                onChange={(gap) => patch({ gap: Math.max(0, gap) })}
              />
              <SliderField
                label="Corner radius"
                value={layer.cornerRadius}
                disabled={locked}
                min={0}
                max={200}
                onChange={(cornerRadius) => patch({ cornerRadius: Math.max(0, cornerRadius) })}
              />
            </>
          ) : null}

          {layer.type === 'text' ? (
            <>
              <TextField
                label="Font"
                value={layer.fontFamily}
                disabled={locked}
                onChange={(fontFamily) => patch({ fontFamily })}
              />
              <SegmentedField
                label="Align"
                value={layer.align}
                options={TEXT_ALIGNMENTS}
                disabled={locked}
                onChange={(align: TextAlignment) => patch({ align })}
              />
              <SliderField
                label="Size"
                value={layer.fontSize}
                disabled={locked}
                min={8}
                max={400}
                onChange={(fontSize) => patch({ fontSize })}
              />
              <SliderField
                label="Weight"
                value={layer.fontWeight}
                disabled={locked}
                min={100}
                max={900}
                step={100}
                onChange={(fontWeight) => patch({ fontWeight })}
              />
              <SliderField
                label="Line height"
                value={layer.lineHeight}
                disabled={locked}
                min={0.5}
                max={3}
                step={0.05}
                format={(value) => value.toFixed(2)}
                onChange={(lineHeight) => patch({ lineHeight })}
              />
              <SliderField
                label="Stroke"
                value={layer.strokeWidth}
                disabled={locked}
                min={0}
                max={40}
                onChange={(strokeWidth) => patch({ strokeWidth })}
              />
              <ColorField
                label="Colour"
                value={layer.color}
                disabled={locked}
                allowNone={false}
                onChange={(color) => patch({ color: color ?? '#ffffff' })}
              />
              <ColorField
                label="Stroke colour"
                value={layer.strokeColor}
                disabled={locked}
                allowNone
                onChange={(strokeColor) => patch({ strokeColor })}
              />
            </>
          ) : null}

          {layer.type === 'shape' ? (
            <>
              <SegmentedField
                label="Shape"
                value={layer.shape}
                options={SHAPE_KINDS}
                disabled={locked}
                onChange={(shape: ShapeKind) => patch({ shape })}
              />
              <SliderField
                label="Stroke"
                value={layer.strokeWidth}
                disabled={locked}
                min={0}
                max={40}
                onChange={(strokeWidth) => patch({ strokeWidth })}
              />
              <ColorField
                label="Fill"
                value={layer.fill}
                disabled={locked}
                allowNone
                onChange={(fill) => patch({ fill })}
              />
              <ColorField
                label="Stroke colour"
                value={layer.strokeColor}
                disabled={locked}
                allowNone
                onChange={(strokeColor) => patch({ strokeColor })}
              />
              {layer.shape === 'rect' ? (
                <SliderField
                  label="Radius"
                  value={layer.cornerRadius}
                  disabled={locked}
                  min={0}
                  max={400}
                  onChange={(cornerRadius) => patch({ cornerRadius })}
                />
              ) : null}
            </>
          ) : null}
        </div>

        {layer.type === 'video' || layer.type === 'screenshare' ? (
          <div className="prop-checks">
            {layer.type === 'video' ? (
              <CheckField
                label="Mirror"
                checked={layer.mirrored}
                disabled={locked}
                onChange={(mirrored) => patch({ mirrored })}
              />
            ) : null}
            {layer.crop ? (
              <button
                type="button"
                className="prop-link"
                disabled={locked}
                onClick={() => patch({ crop: null })}
              >
                Reset crop
              </button>
            ) : null}
          </div>
        ) : null}
      </Section>
    </div>
  );
}

/** Kept for the fit selector's type, which the union cannot infer on its own. */
export type { FitMode };
